import { Mail, MapPin, Phone, ChevronRight, ChevronDown, X } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import allProductsData from '../../data/products.json';
import categoriesData from '../../data/categories.json';
import { useState } from 'react';

interface Category {
  id: string;
  name: string;
  url: string;
  parentId?: string;
  type?: string;
}

interface ProductDetailProps {
  productId: number;
  onNavigate: (page: string, params?: any) => void;
}

// 从 JSON 文件导入产品数据
const allProducts = allProductsData;

const categories: Category[] = categoriesData;

export function ProductDetail({ productId, onNavigate }: ProductDetailProps) {
  const product = allProducts.find(p => p.id === productId);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    product ? new Set([product.category]) : new Set()
  );
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  if (!product) {
    return (
      <div className="py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl mb-8">Product Not Found</h1>
          <button
            onClick={() => onNavigate('Product')}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-lg"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  // Get latest products (excluding current product)
  const latestProducts = allProducts.filter(p => p.id !== productId).slice(0, 3);

  // 获取第一级分类（品类）
  const mainCategories = categories.filter(cat => cat.type === 'category' || (!cat.parentId && cat.id !== 'all'));
  
  // 获取第二级分类（品牌）
  const brands = categories.filter(cat => cat.type === 'brand' || (cat.parentId && cat.parentId !== undefined));

  // 获取当前选中品类下的品牌
  const getBrandsForCategory = (categoryId: string) => {
    return brands.filter(brand => brand.parentId === categoryId);
  };

  const handleCategoryClick = (categoryId: string) => {
    // 切换展开状态
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
    onNavigate('Product', { category: categoryId });
  };

  const handleBrandClick = (brandId: string, categoryId: string) => {
    onNavigate('Product', { category: categoryId, brand: brandId });
  };

  const handleAllProductsClick = () => {
    onNavigate('Product');
  };

  return (
    <div className="py-12">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-8">
          <button onClick={() => onNavigate('Home')} className="hover:text-primary">
            Home
          </button>
          <ChevronRight size={16} />
          <button onClick={() => onNavigate('Product')} className="hover:text-primary">
            Products
          </button>
          <ChevronRight size={16} />
          <span className="text-gray-900">{product.name}</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar */}
          <aside className="lg:w-80 flex-shrink-0 space-y-6">
            {/* Categories */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl mb-4">Categories</h2>
              
              {/* All Products */}
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={handleAllProductsClick}
                    className="w-full text-left px-4 py-2 rounded-lg transition-colors hover:bg-gray-100 text-gray-700"
                  >
                    All Products
                  </button>
                </li>

                {/* 第一级分类（品类） */}
                {mainCategories.map((category) => {
                  const categoryBrands = getBrandsForCategory(category.id);
                  const isExpanded = expandedCategories.has(category.id);
                  const isSelected = product.category === category.id && !product.brand;
                  
                  return (
                    <li key={category.id}>
                      <button
                        onClick={() => handleCategoryClick(category.id)}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center justify-between ${
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <span>{category.name}</span>
                        {categoryBrands.length > 0 && (
                          <span>
                            {isExpanded ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </span>
                        )}
                      </button>
                      
                      {/* 第二级分类（品牌） */}
                      {isExpanded && categoryBrands.length > 0 && (
                        <ul className="mt-1 ml-4 space-y-1">
                          <li>
                            <button
                              onClick={() => onNavigate('Product', { category: category.id })}
                              className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
                                isSelected
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-gray-50 text-gray-600'
                              }`}
                            >
                              All {category.name}
                            </button>
                          </li>
                          {categoryBrands.map((brand) => {
                            const isBrandSelected = product.brand === brand.id;
                            return (
                              <li key={brand.id}>
                                <button
                                  onClick={() => handleBrandClick(brand.id, category.id)}
                                  className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
                                    isBrandSelected
                                      ? 'bg-primary text-primary-foreground'
                                      : 'hover:bg-gray-50 text-gray-600'
                                  }`}
                                >
                                  {brand.name}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Latest Products */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl mb-4">Latest Products</h2>
              <div className="space-y-4">
                {latestProducts.map((latestProduct) => (
                  <div
                    key={latestProduct.id}
                    onClick={() => onNavigate('ProductDetail', { productId: latestProduct.id })}
                    className="flex gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                  >
                    <div className="w-20 h-20 flex-shrink-0 bg-gray-50 rounded">
                      <ImageWithFallback
                        src={latestProduct.image}
                        alt={latestProduct.name}
                        className="w-full h-full object-contain rounded"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm mb-1 line-clamp-2">{latestProduct.name}</h3>
                      <p className="text-primary">{latestProduct.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Us */}
            <div className="bg-primary text-primary-foreground rounded-lg shadow-md p-6">
              <h2 className="text-2xl mb-4">Contact Us</h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Phone size={18} />
                  <span>+86 400-882-1928</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={18} />
                  <span>dch-equips@outlook.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin size={18} />
                  <span>NO.1990-1, Jinbi Road, Fengxian District, Shanghai</span>
                </div>
              </div>
              <button
                onClick={() => onNavigate('Contact Us')}
                className="w-full mt-4 bg-white text-primary hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors"
              >
                Send Message
              </button>
            </div>
          </aside>

          {/* Right Content - Product Details */}
          <main className="flex-1">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Product Image */}
              <div 
                className="relative h-96 bg-gray-50 cursor-zoom-in flex items-center justify-center"
                onClick={() => setIsImageModalOpen(true)}
              >
                <ImageWithFallback
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
                  <span className="opacity-0 hover:opacity-100 text-white bg-black/50 px-4 py-2 rounded-lg transition-opacity">
                    Click to enlarge
                  </span>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-8">
                <div className="mb-6">
                  <span className="inline-block bg-primary/20 text-primary px-3 py-1 rounded-full text-sm mb-3">
                    {product.categoryName}
                  </span>
                  <h1 className="text-4xl mb-3">{product.name}</h1>
                  <p className="text-gray-600 mb-4">Part No: {product.partNo}</p>
                  <div className="text-3xl text-primary mb-6">{product.price}</div>
                </div>

                {/* Description */}
                <div className="mb-8">
                  <h2 className="text-2xl mb-4">Product Description</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">{product.fullDescription}</p>
                  <p className="text-gray-700 leading-relaxed">{product.description}</p>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h2 className="text-2xl mb-4">Key Features</h2>
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <span className="text-primary mt-1">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Specifications */}
                <div className="mb-8">
                  <h2 className="text-2xl mb-4">Technical Specifications</h2>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <table className="w-full">
                      <tbody>
                        {Object.entries(product.specifications).map(([key, value], index) => (
                          <tr key={index} className="border-b border-gray-200 last:border-0">
                            <td className="py-3 pr-4 font-semibold text-gray-700 w-1/3">{key}</td>
                            <td className="py-3 text-gray-600">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => onNavigate('Contact Us')}
                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-lg transition-colors"
                  >
                    Request Quote
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsImageModalOpen(false)}
        >
          <button
            onClick={() => setIsImageModalOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
          >
            <X size={32} />
          </button>
          <div className="max-w-5xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <ImageWithFallback
              src={product.image}
              alt={product.name}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
